const axios = require('axios');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PayyoCancelPaymentRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoCancelPaymentRequestRecord');
const PayyoCapturePaymentRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoCapturePaymentRequestRecord');
const PayyoCreatePaymentRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoCreatePaymentRequestRecord');
const PayyoCreateRefundRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoCreateRefundRequestRecord');
const PayyoDeviceListRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoDeviceListRequestRecord');
const PayyoDeviceRegistrationRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoDeviceRegistrationRequestRecord');
const PayyoDeviceRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoDeviceRequestRecord');
const PayyoGetPaymentRefundsRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoGetPaymentRefundsRequestRecord');
const PayyoGetPaymentRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoGetPaymentRequestRecord');
const PayyoMerchantConfigureTipsRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoMerchantConfigureTipsRequestRecord');
const PayyoMerchantRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoMerchantRequestRecord');
const PayyoPaymentStatus = require('@/lib/PaymentMethods/Payyo/Enums/PayyoPaymentStatus');
const PayyoSimulatePaymentRequestRecord = require('@/lib/PaymentMethods/Payyo/Records/Request/PayyoSimulatePaymentRequestRecord');
const PayyoSocketResponseType = require('@/lib/PaymentMethods/Payyo/Enums/PayyoSocketResponseType');
const PayyoTransaction = require('@/lib/PaymentMethods/Payyo/PayyoTransaction');

class PayyoDriver {
    /**
     * @param {PayyoConfig} payyoConfig
     * @param {CurrentDevice} currentDevice
     * @param {PayyoWebSocketClient} payyoWebSocketClient
     * @param {ReceiptPrinter} receiptPrinter
     * @param {ToastFactory} toastFactory
     */
    constructor(
        payyoConfig,
        currentDevice,
        payyoWebSocketClient,
        receiptPrinter,
        toastFactory
    ) {
        /**
         * @type {ConsoleLogger}
         */
        this.logger = logFactory('PayyoDriver');

        /**
         * @type {CurrentDevice}
         */
        this.currentDevice = currentDevice;

        /**
         * @type {ReceiptPrinter}
         */
        this.receiptPrinter = receiptPrinter;

        /**
         * @type {ToastFactory}
         */
        this.toastFactory = toastFactory;

        /**
         * @type {string}
         */
        this.paymentMethodRef = PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN;

        /**
         * @type {PayyoConfig}
         */
        this.config = payyoConfig;

        /**
         * @type {AxiosInstance}
         */
        this.axiosInstance = null;

        /**
         * @type {PayyoWebSocketClient}
         */
        this.payyoWebSocketClient = payyoWebSocketClient;

        /**
         * @type {PayyoTransaction}
         */
        this.currentTransaction = null;
    }

    /**
     * Should we validate the terminalId or not?
     *
     * @param {boolean} validateDeviceId
     */
    init(validateDeviceId = true) {
        console.log('PayyoDriver init', validateDeviceId);
        this.config.validate(validateDeviceId);
        this.axiosInstance = this.createAxiosInstance();

        if (validateDeviceId) {
            this.payyoWebSocketClient.connect();
        }
    }

    /**
     * Create an axios instance with all the required headers and baseURL set.
     *
     * @returns {AxiosInstance}
     */
    createAxiosInstance() {
        return axios.create({
            baseURL: this.config.apiBaseUrl + this.config.apiUrlPath,
            headers: {
                Authorization: this.config.apiKey,
            },
        });
    }

    async connectWebSocket() {
        // Register an onMessage handler for the websocket.
        // This may be called multiple times, but it's safe to overwrite the previous handler because they
        // do the same thing. This also helps if somehow we got different instances of PayyoDriver that the correct
        // one receives the messages.
        this.payyoWebSocketClient.onMessage = (message) => {
            this.handleWebsocketMessage(message);
        };

        // Connect to the Payyo WebSocket.
        // If the socket is already connected, it won't connect again it will reuse the existing connection.
        await this.payyoWebSocketClient.connect();
    }

    /**
     * Handle a message received from the websocket.
     *
     * @param {{}} message
     */
    handleWebsocketMessage(message) {
        try {
            if (message.data) {
                const event = JSON.parse(message.data);
                if (event) {
                    switch (event.type) {
                        case PayyoSocketResponseType.PAYMENT_FAILED:
                        // Something has gone wrong. This may or may not be recoverable.
                        // The PayyoPaymentMethod component now needs to trigger a device request so that
                        // we can see if this is recoverable or not.

                        // eslint-disable-next-line no-fallthrough
                        case PayyoSocketResponseType.PAYMENT_CREATED:
                        case PayyoSocketResponseType.PAYMENT_AUTHORIZED:
                        case PayyoSocketResponseType.PAYMENT_SUCCEEDED:
                        case PayyoSocketResponseType.PAYMENT_CANCELED:
                        case PayyoSocketResponseType.PAYMENT_REFUNDED:
                            if (this.currentTransaction && event.payment) {
                                let response = { payment: event.payment, type: event.type };
                                this.currentTransaction.addTransactionResponse(response);
                            } else if (this.currentTransaction && event.refund) {
                                // Note: A refund from the websocket no longer contains the original payment.
                                let response = { refund: event.refund, type: event.type };
                                this.currentTransaction.addTransactionResponse(response);
                            } else {
                                console.log('Websocket Payment message received with no current transaction');
                            }
                            break;
                        case PayyoSocketResponseType.DEVICE_STATUS:
                            // We aren't currently carrying out any action that would result in this response, so
                            // it is just here for completeness.
                            break;
                        case PayyoSocketResponseType.ERROR:
                            // An error with Payyo / Stripe rather than a network error for example.
                            break;
                        default:
                            console.log('Websocket message received with unknown event Type');
                    }
                }
            }
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Get data about the current merchant specified by the config.
     *
     * @return {Promise<*|null>}
     */
    async getMerchant() {
        try {
            const response = await this.axiosInstance.request(
                new PayyoMerchantRequestRecord(this.config.merchantId),
            );

            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * @param {string} deviceId
     *
     * @return {Promise<*|null>}
     */
    async getDevice(deviceId) {
        try {
            const response = await this.axiosInstance.request(
                new PayyoDeviceRequestRecord(deviceId),
            );

            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * Return an array of all the devices (terminals) registered to the current merchant.
     *
     * @return {Promise<*|null>}
     */
    async getDevices() {
        try {
            const response = await this.axiosInstance.request(
                new PayyoDeviceListRequestRecord(this.config.merchantId),
            );

            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * @param {{}} registrationData
     *
     * @return {Promise<*|null>}
     */
    async registerDevice(registrationData) {
        try {
            const response = await this.axiosInstance.request(
                new PayyoDeviceRegistrationRequestRecord(registrationData)
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * Construct a new PayyoTransaction object.
     * This does not send any requests, it just returns the object.
     *
     * @param {number} amount
     *
     * @returns {PayyoTransaction}
     */
    createTransaction(amount) {
        return new PayyoTransaction(amount);
    }

    /**
     * Initiate a payment transaction with Payyo.
     *
     * @param {PayyoTransaction} payyoTransaction
     *
     * @return {Promise<null|boolean>}
     */
    async createPayment(payyoTransaction) {
        try {
            await this.connectWebSocket();
            this.currentTransaction = payyoTransaction;
            const paymentData = {
                amount: payyoTransaction.amountInMinorUnits,
                device_id: this.config.deviceId,
                currency: 'gbp',
                reference: payyoTransaction.thirdPartyID,
                description: payyoTransaction.description,
                process_config: {
                    skip_tipping: !payyoTransaction.promptForTips,
                },
                payment_methods: payyoTransaction.payment_methods,
            };
            const response = await this.axiosInstance.request(
                new PayyoCreatePaymentRequestRecord(paymentData)
            );
            payyoTransaction.addTransactionResponse(response);
            // We wait for updates on the websocket.
            return true;
        } catch (error) {
            console.log('Payyo createPayment error', error, error?.response?.data);
            if (error.response?.data?.error === 'payment_not_processed') {
                error.response.data.error = 'Please check the terminal as it may be updating and try again when the terminal is ready.';
            }
            this.currentTransaction = null;
            this.handleError(error);
            return null;
        }
    }

    /**
     * Attempt a refund against an existing Payyo payment.
     *
     * @param {PayyoTransaction} payyoTransaction
     *
     * @return {Promise<null|boolean>}
     */
    async createRefund(payyoTransaction) {
        try {
            await this.connectWebSocket();
            this.currentTransaction = payyoTransaction;
            const paymentData = {
                amount: payyoTransaction.amountInMinorUnits * -1,
                paymentId: payyoTransaction.paymentId,
            };
            // eslint-disable-next-line unused-imports/no-unused-vars
            const response = await this.axiosInstance.request(
                new PayyoCreateRefundRequestRecord(paymentData)
            );
            // Unlike payments refunds do not require capture. The result is usually either success or failure.
            // However, the data returned by the POST request is much thinner than the data returned from the
            // websocket so let's just ignore this first request.
            return true;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * A request to get the current status of a payment.
     *
     * @param {string} paymentId
     *
     * @return {Promise<*|null>}
     */
    async getPayment(paymentId) {
        try {
            const response = await this.axiosInstance.request(
                new PayyoGetPaymentRequestRecord(paymentId)
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * A request to get any refunds for the specified payment.
     * Returns an empty array if there are no refunds.
     *
     * @param {string} paymentId
     *
     * @return {Promise<[*]|null>}
     */
    async getPaymentRefunds(paymentId) {
        try {
            const response = await this.axiosInstance.request(
                new PayyoGetPaymentRefundsRequestRecord(paymentId)
            );
            return response.data.refunds;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * The customer has authorised the payment by tap or pin. We now need to capture (confirm) the payment.
     * A payment can be cancelled up to the point it is captured after this it has to be refunded.
     *
     * @param {PayyoTransaction} payyoTransaction
     *
     * @return {Promise<void>}
     */
    async capturePayment(payyoTransaction) {
        try {
            this.currentTransaction = payyoTransaction;
            const response = await this.axiosInstance.request(
                new PayyoCapturePaymentRequestRecord(payyoTransaction.paymentId)
            );

            payyoTransaction.addTransactionResponse(response);
        } catch (error) {
            if (error.response) {
                this.currentTransaction.addError(PayyoPaymentStatus.CAPTURE_ERROR, error.response);
                return;
            }
            this.handleError(error);
        }
    }

    /**
     * Attempt to cancel a payment that has not yet been captured.
     * Only a payment with one of the following statuses can be cancelled:
     *      processing, requires_action, requires_capture, requires_confirmation, requires_payment_method
     * If the status is canceled then obviously the payment is already cancelled so update the UI accordingly.
     * If the status is succeeded then
     *
     * @param {PayyoTransaction} payyoTransaction
     *
     * @return {Promise<void>}
     */
    async cancelPayment(payyoTransaction) {
        try {
            this.currentTransaction = payyoTransaction;
            const response = await this.axiosInstance.request(
                new PayyoCancelPaymentRequestRecord(payyoTransaction.paymentId)
            );
            payyoTransaction.addTransactionResponse(response);
        } catch (error) {
            if (error.response) {
                this.currentTransaction.addError(PayyoPaymentStatus.CANCEL_ERROR, error.response);
                return;
            }
            this.handleError(error);
        }
    }

    /**
     * @param {PayyoTransaction} payyoTransaction
     * @param {string} cardNumber
     *
     * @returns {Promise}
     */
    async simulatePayment(payyoTransaction, cardNumber) {
        // There is no error handling around this because this should not affect the flow of the transaction.
        // This is a side request that will trigger response from the Payyo API in the actual flow.
        await this.axiosInstance.request(
            new PayyoSimulatePaymentRequestRecord(
                payyoTransaction.paymentId,
                cardNumber
            )
        );
    }

    async updateTipsConfig(tipsConfig) {
        try {
            const response = await this.axiosInstance.request(
                new PayyoMerchantConfigureTipsRequestRecord(this.config.merchantId, tipsConfig)
            );

            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * When we receive a 4xx error then we need to display the error and message to the user.
     * When we receive a 5xx error then just display a generic message.
     *
     * @see https://axios-http.com/docs/handling_errors
     *
     * @see https://axios-http.com/docs/handling_errors
     *
     * @param error
     */
    handleError(error) {
        console.log(error);
        let userMessage = null;

        if (error?.response?.data?.error) {
            // The request was made and the server responded with a status code.
            userMessage = error.response.data.error;
            if (error.response.data.message) {
                userMessage = userMessage + ': ' + error.response.data.message;
            }
        }

        if (!userMessage && error.request) {
            // The request was made but no response was received.
            userMessage = 'The Payyo API is not responding. There may be a problem with the API.';
        }

        if (!userMessage && error.message) {
            userMessage = error.message;
        }

        if (!userMessage) {
            userMessage = 'Payyo API error. Please check that your settings are correct.';
        }

        throw new Error(userMessage);
    }
}

module.exports = PayyoDriver;
